/**
 * Who may do what.
 *
 * Modelled on the Instec Platform's IAM, with one substitution: where that
 * grants access per *solution*, this grants it per **area** - the three
 * surfaces this product actually has. The rest is deliberately the same
 * shape, because the same shape is what makes it recognisable to anyone who
 * administers both.
 *
 * There is no separate `enabled` flag as the Platform has. There it decides
 * whether a solution's tile appears at all, which is a question a
 * single-product portal does not have; `view` answers it here.
 */
export const AREAS = ["configuration", "products", "applications", "accounts"];
export const CAPABILITIES = ["view", "edit", "delete"];
export const NO_ACCESS = { view: false, edit: false, delete: false };
export const AREA_DESCRIPTIONS = [
    {
        area: "configuration",
        label: "Field library",
        surface: "config",
        view: "See entities, value domains and fields",
        edit: "Define and change them",
        delete: "Remove ones nothing uses",
    },
    {
        area: "products",
        label: "Products",
        surface: "config",
        view: "See products and their versions",
        edit: "Build drafts, attach fields and publish",
        delete: "Remove sections, attachments and drafts",
    },
    {
        area: "applications",
        label: "Applications",
        surface: "portal",
        view: "See applications and what they hold",
        edit: "Start them, answer them and submit",
        delete: "Withdraw them",
    },
    {
        area: "accounts",
        label: "Accounts",
        surface: "both",
        view: "See who has an account and what they may do",
        edit: "Create accounts, set permissions, reset passwords",
        delete: "Remove accounts",
    },
];
/** Every area switched off. What a new account gets before anyone ticks a box. */
export function emptyGrants() {
    return Object.fromEntries(AREAS.map((area) => [area, { ...NO_ACCESS }]));
}
/**
 * Fill in whatever is missing, so a caller always gets a complete object.
 *
 * An area added to this list tomorrow is absent from every grant row written
 * today; absent has to read as *denied*, never as "unspecified, allow it".
 */
export function completeGrants(partial) {
    const complete = emptyGrants();
    if (!partial)
        return complete;
    for (const area of AREAS) {
        const given = partial[area];
        if (!given)
            continue;
        complete[area] = {
            view: given.view === true,
            edit: given.edit === true,
            delete: given.delete === true,
        };
    }
    return complete;
}
/**
 * Whether this person may do this.
 *
 * An administrator may do everything - that is the only inheritance, and it is
 * checked first so an admin can never lock themselves out of the accounts
 * screen by mis-ticking their own boxes.
 *
 * Editing implies viewing, and deleting implies editing. Granting "edit"
 * without "view" is a state an administrator can tick into by accident, and it
 * would mean someone who can change a thing they cannot see.
 */
export function allows(person, area, capability) {
    if (!person)
        return false;
    if (person.isAdmin)
        return true;
    const grant = person.grants?.[area];
    if (!grant)
        return false;
    if (capability === "view")
        return grant.view || grant.edit || grant.delete;
    if (capability === "edit")
        return grant.edit || grant.delete;
    return grant.delete;
}
/** The areas this person can open at all, for drawing navigation. */
export function visibleAreas(person) {
    return AREAS.filter((area) => allows(person, area, "view"));
}
const grant = (view, edit, remove = false) => ({ view, edit, delete: remove });
export const PRESETS = [
    {
        key: "config_engineer",
        label: "Configuration engineer",
        description: "Builds the field library and the products. Can see applications but not fill them in.",
        grants: {
            configuration: grant(true, true, true),
            products: grant(true, true, true),
            applications: grant(true, false),
            accounts: { ...NO_ACCESS },
        },
    },
    {
        key: "underwriter",
        label: "Underwriter",
        description: "Fills in and submits applications. Sees the products, does not change them.",
        grants: {
            configuration: grant(true, false),
            products: grant(true, false),
            applications: grant(true, true, true),
            accounts: { ...NO_ACCESS },
        },
    },
    {
        key: "viewer",
        label: "Viewer",
        description: "Reads everything, changes nothing.",
        grants: {
            configuration: grant(true, false),
            products: grant(true, false),
            applications: grant(true, false),
            accounts: { ...NO_ACCESS },
        },
    },
    {
        key: "none",
        label: "No access",
        description: "An account that exists and can sign in, but can open nothing yet.",
        grants: emptyGrants(),
    },
];
//# sourceMappingURL=permissions.js.map